import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FaCloudRain, FaCloudSun, FaCloudSunRain, FaTemperatureHigh, FaTint, FaWind } from 'react-icons/fa';
import {
  formatHourlyDay,
  formatHourlyTime,
  groupHourlyForecastByDay,
  summarizeHourlyDay,
} from './hourlyForecastService';

function weatherIcon(rain) {
  if (rain >= 1) return <FaCloudRain aria-hidden="true" />;
  if (rain > 0) return <FaCloudSunRain aria-hidden="true" />;
  return <FaCloudSun aria-hidden="true" />;
}

function metric(value, suffix, digits = 1) {
  return value === null || value === undefined ? '-' : `${Number(value).toFixed(digits)}${suffix}`;
}

export default function HourlyForecast({ rows, dailyRows, loading, error, darkMode, isMobile }) {
  const dayGroups = useMemo(() => groupHourlyForecastByDay(rows, 4, dailyRows), [rows, dailyRows]);
  const [selectedDay, setSelectedDay] = useState(null);

  useEffect(() => {
    if (!dayGroups.some(day => day.key === selectedDay)) {
      setSelectedDay(dayGroups[0]?.key || null);
    }
  }, [dayGroups, selectedDay]);

  const selectedGroup = dayGroups.find(day => day.key === selectedDay) || dayGroups[0];
  const summary = useMemo(() => summarizeHourlyDay(selectedGroup?.hours), [selectedGroup]);

  if (loading) {
    return <div className="forecast-hourly-state">Carregando previsão horária...</div>;
  }

  if (error) {
    return <div className="forecast-hourly-state is-error">Não foi possível carregar a previsão horária. {error}</div>;
  }

  if (!selectedGroup || !summary) {
    return <div className="forecast-hourly-state">Não há dados horários do GFS para este município.</div>;
  }

  const chartData = selectedGroup.hours.map(hour => ({
    ...hour,
    hourLabel: formatHourlyTime(hour.timestamp),
  }));

  return (
    <section className="forecast-hourly" aria-labelledby="forecast-hourly-title">
      <header className="forecast-hourly-heading">
        <div>
          <span>Detalhamento GFS</span>
          <h2 id="forecast-hourly-title">Previsão por hora</h2>
          <p>Temperatura, chuva, umidade e vento no horário local para planejar as próximas operações.</p>
        </div>
        <span className="forecast-hourly-resolution">Atualização horária</span>
      </header>

      <div className="forecast-hourly-days" role="tablist" aria-label="Dias da previsão horária">
        {dayGroups.map((day, index) => {
          const daySummary = summarizeHourlyDay(day.hours);
          return (
            <button
              key={day.key}
              type="button"
              role="tab"
              aria-selected={day.key === selectedGroup.key}
              className={day.key === selectedGroup.key ? 'is-active' : ''}
              onClick={() => setSelectedDay(day.key)}
            >
              <span>{index === 0 ? 'Próximo período' : formatHourlyDay(day.key, { weekday: 'short' })}</span>
              <strong>{formatHourlyDay(day.key, { day: '2-digit', month: '2-digit' })}</strong>
              <small><FaCloudRain aria-hidden="true" /> {daySummary.rain.toFixed(1)} mm</small>
            </button>
          );
        })}
      </div>

      <div className="forecast-hourly-kpis" aria-label="Resumo do dia selecionado">
        <article><span><FaCloudRain /> Chuva acumulada</span><strong>{summary.rain.toFixed(1)} mm</strong></article>
        <article><span><FaTemperatureHigh /> Faixa térmica</span><strong>{metric(summary.minimumTemperature, '°', 0)} <small>a</small> {metric(summary.maximumTemperature, '°C', 0)}</strong></article>
        <article><span><FaTint /> Menor umidade</span><strong>{metric(summary.minimumHumidity, '%', 0)}</strong></article>
        <article><span><FaWind /> Vento máximo</span><strong>{metric(summary.maximumWind, ' m/s')}</strong></article>
      </div>

      <div className="forecast-hourly-chart">
        <div className="forecast-hourly-chart-title">
          <strong>Comportamento ao longo do dia</strong>
          <span>{formatHourlyDay(selectedGroup.key, { weekday: 'long', day: '2-digit', month: 'long' })}</span>
        </div>
        <ResponsiveContainer width="100%" height={isMobile ? 250 : 300}>
          <ComposedChart data={chartData} margin={{ top: 8, right: isMobile ? 0 : 16, left: isMobile ? -22 : 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={darkMode ? '#33455c' : '#dce8f4'} />
            <XAxis dataKey="hourLabel" tick={{ fill: darkMode ? '#9db2ca' : '#5d728b', fontSize: 11 }} minTickGap={isMobile ? 24 : 12} />
            <YAxis yAxisId="temperature" tick={{ fill: darkMode ? '#9db2ca' : '#5d728b', fontSize: 11 }} unit="°" />
            <YAxis yAxisId="rain" orientation="right" tick={{ fill: darkMode ? '#9db2ca' : '#5d728b', fontSize: 11 }} unit=" mm" width={isMobile ? 44 : 54} />
            <Tooltip
              contentStyle={{ background: darkMode ? '#152131' : '#fff', border: `1px solid ${darkMode ? '#263b55' : '#d7e5f4'}`, borderRadius: 10 }}
              formatter={(value, name) => [name === 'Chuva' ? `${Number(value).toFixed(1)} mm` : `${Number(value).toFixed(1)}°C`, name]}
            />
            <Legend />
            <Bar yAxisId="rain" dataKey="precipitation" name="Chuva" fill="#13a67a" radius={[4, 4, 0, 0]} maxBarSize={18} />
            <Line yAxisId="temperature" type="monotone" dataKey="temperature" name="Temperatura" stroke="#e15b3d" strokeWidth={2.5} dot={false} connectNulls />
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      <div className="forecast-hourly-strip" aria-label="Condições por hora">
        {selectedGroup.hours.map(hour => (
          <article key={hour.datetime_utc}>
            <time dateTime={hour.datetime_utc}>{formatHourlyTime(hour.timestamp)}</time>
            <span className="forecast-hourly-weather">{weatherIcon(hour.precipitation)}</span>
            <strong>{metric(hour.temperature, '°C')}</strong>
            <span><FaCloudRain /> {metric(hour.precipitation, ' mm')}</span>
            <span><FaTint /> {metric(hour.humidity, '%', 0)}</span>
            <span><FaWind /> {metric(hour.wind, ' m/s')}</span>
          </article>
        ))}
      </div>
    </section>
  );
}